import { motion, AnimatePresence } from "framer-motion";
import { OptionCard, TextInput } from "../shared";
import { MarkdownContent } from "@/components/ui/markdown-content";
import type {
  ChooseAdaptiveJustifySpec,
  ChooseAdaptiveJustifyAnswer,
  DecisionOption,
} from "@/types/hld-wizard";
import { cn } from "@/lib/utils";

const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.3 },
};

interface ChooseAdaptiveJustifyDecisionProps {
  spec: ChooseAdaptiveJustifySpec;
  context: string;
  question: string;
  answer: ChooseAdaptiveJustifyAnswer | undefined;
  onChange: (answer: ChooseAdaptiveJustifyAnswer) => void;
  disabled?: boolean;
}

export function ChooseAdaptiveJustifyDecision({
  spec,
  context,
  question,
  answer,
  onChange,
  disabled,
}: ChooseAdaptiveJustifyDecisionProps) {
  const selectedOptionId = answer?.option_id ?? "";
  const selectedSecondaryId = answer?.secondary_option_id ?? "";

  // Find the selected secondary option to get its justify_prompt
  const selectedSecondary = spec.secondary.options.find(
    (o) => o.id === selectedSecondaryId,
  );

  function selectPrimary(optionId: string) {
    onChange({
      option_id: optionId,
      secondary_option_id: "",
      secondary_justify_text: "",
      tertiary_option_id: undefined,
    });
  }

  function selectSecondary(optionId: string) {
    if (!answer) return;
    onChange({
      ...answer,
      secondary_option_id: optionId,
      secondary_justify_text: "",
      tertiary_option_id: undefined,
    });
  }

  function setJustifyText(text: string) {
    if (!answer) return;
    onChange({ ...answer, secondary_justify_text: text });
  }

  function selectTertiary(optionId: string) {
    if (!answer) return;
    onChange({ ...answer, tertiary_option_id: optionId });
  }

  return (
    <div className="space-y-6">
      {context && (
        <div className="border-l-2 border-primary/30 pl-4 text-muted-foreground">
          <MarkdownContent>{context}</MarkdownContent>
        </div>
      )}

      <MarkdownContent className="font-medium">{question}</MarkdownContent>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {spec.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            selected={selectedOptionId === opt.id}
            onClick={() => selectPrimary(opt.id)}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Secondary choice */}
      <AnimatePresence>
        {selectedOptionId && (
          <motion.div {...fadeIn} className="space-y-4">
            <p className="font-medium">{spec.secondary.question}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {spec.secondary.options.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => selectSecondary(opt.id)}
                  disabled={disabled}
                  className={cn(
                    "rounded-lg border p-4 text-left transition-all",
                    selectedSecondaryId === opt.id
                      ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                      : "border-border hover:border-primary/40",
                    disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <div className="font-semibold">{opt.label}</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Justify text (adaptive prompt based on secondary choice) */}
      <AnimatePresence>
        {selectedSecondary && (
          <motion.div {...fadeIn}>
            <TextInput
              value={answer?.secondary_justify_text ?? ""}
              onChange={setJustifyText}
              placeholder={selectedSecondary.justify_prompt}
              maxChars={spec.secondary.max_chars}
              disabled={disabled}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tertiary choice (optional) */}
      <AnimatePresence>
        {spec.tertiary &&
          answer &&
          answer.secondary_justify_text.length > 10 && (
            <motion.div {...fadeIn} className="space-y-4">
              <p className="font-medium">{spec.tertiary.question}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {spec.tertiary.options.map((opt: DecisionOption) => (
                  <OptionCard
                    key={opt.id}
                    option={opt}
                    selected={answer.tertiary_option_id === opt.id}
                    onClick={() => selectTertiary(opt.id)}
                    disabled={disabled}
                  />
                ))}
              </div>
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
}
